import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, Search, Loader2, UserPlus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";

const riskColor = (pct: number) =>
  pct >= 75 ? "text-green-600 bg-green-50" : pct >= 50 ? "text-yellow-600 bg-yellow-50" : "text-red-600 bg-red-50";
const riskLabel = (pct: number, bn: boolean) =>
  pct >= 75 ? (bn ? "কম ঝুঁকি" : "Low Risk") : pct >= 50 ? (bn ? "মাঝারি" : "Medium") : (bn ? "উচ্চ ঝুঁকি" : "High Risk");

const PatientManagement = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data: convData, error } = await supabase
        .from("conversations")
        .select("patient_id")
        .eq("practitioner_id", user.id)
        .eq("practitioner_type", "dietitian");

      if (error || !convData) { setLoading(false); return; }

      const patientIds = [...new Set(convData.map((c: any) => c.patient_id))];
      if (patientIds.length === 0) { setLoading(false); return; }

      const [{ data: profileData }, { data: taskData }] = await Promise.all([
        supabase.from("profiles").select("user_id, full_name, age, health_profile").in("user_id", patientIds),
        supabase.from("tasks").select("patient_id, done").in("patient_id", patientIds),
      ]);

      const taskMap: Record<string, { done: number; total: number }> = {};
      (taskData ?? []).forEach((t: any) => {
        if (!taskMap[t.patient_id]) taskMap[t.patient_id] = { done: 0, total: 0 };
        taskMap[t.patient_id].total++;
        if (t.done) taskMap[t.patient_id].done++;
      });

      setPatients(
        (profileData ?? []).map((p) => {
          const tm = taskMap[p.user_id];
          const pct = tm && tm.total > 0 ? Math.round((tm.done / tm.total) * 100) : null;
          return { id: p.user_id, ...p, compliancePct: pct };
        })
      );
      setLoading(false);
    };
    load();
  }, [user]);

  const filtered = patients.filter(p => (p.full_name ?? "").toLowerCase().includes(search.toLowerCase()));
  const container = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
  const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

  return (
    <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={bn ? "রোগী খুঁজুন..." : "Search patients..."} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card/60 backdrop-blur-sm border-border/40" />
        </div>
        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
          <Users className="h-3 w-3 mr-1" />{patients.length} {bn ? "রোগী" : "patients"}
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card/60 backdrop-blur-sm border-border/40">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e4f1eb]">
              <UserPlus className="h-8 w-8 text-primary/60" />
            </div>
            <p className="text-sm font-semibold text-foreground/70">{bn ? "কোন রোগী পাওয়া যায়নি" : "No patients found"}</p>
            <p className="text-xs text-muted-foreground mt-1">{bn ? "রোগীরা এখানে দেখা যাবে" : "Patients will appear here once added"}</p>
          </CardContent>
        </Card>
      ) : (
        <motion.div className="grid gap-3" variants={container} initial="hidden" animate="show">
          {filtered.map(p => (
            <motion.div key={p.id} variants={item}>
              <Card className="bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all hover:shadow-md cursor-pointer">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e4f1eb] text-sm font-bold text-primary">
                    {(p.full_name ?? "?").charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{p.full_name}</p>
                    <p className="text-[11px] text-muted-foreground">{p.health_profile ?? ""} · {bn ? "বয়স" : "Age"} {p.age ?? "—"}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {p.compliancePct !== null ? (
                      <>
                        <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${riskColor(p.compliancePct)}`}>
                          {p.compliancePct}%
                        </span>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${riskColor(p.compliancePct)}`}>
                          {riskLabel(p.compliancePct, bn)}
                        </span>
                      </>
                    ) : (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {bn ? "কোনো টাস্ক নেই" : "No tasks"}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export default PatientManagement;
